import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Form, Tooltip, OverlayTrigger, ButtonGroup, Dropdown, Toast, ToastContainer, Badge } from 'react-bootstrap'
import { stockItemAPI } from '../api/stockItemAPI'
import { vendorAPI } from '../api/vendorAPI'
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI'
import { brandAPI } from '../api/brandAPI'
import { calculateVolumeCubicFeet, calculateNetCaseWeight, calculateGrossCaseWeight, highlightText } from '../utils/commonUtils'
import ConfirmationModal from '../components/ConfirmationModal'
import PaginationComponent from '../components/Pagination'
import { createColumnToggleDropdown, createFilterDropdown, handleSearchWithDebounce, getCountryFlag, useTableState, getReportColumns } from '../utils/tableUtils'
import ResizableTable from '../components/ResizableTable'
import StickyScrollbar from '../components/StickyScrollbar'
import Select from 'react-select'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'


const UOM_MAP = {
    'bag': 'Bag',
    'bulk': 'Bulk',
    'clear_film': 'Clear Film',
    'ct': 'Count',
    'film': 'Film',
    'jar': 'Jar',
    'pallet': 'Pallet',
    'tub': 'Tub',
    'tote_bag': 'Tote Bag'
};

function StockItemsListScreen() {
    const { hasPermission } = usePermissions()
    const [stockItems, setStockItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [vendorMap, setVendorMap] = useState({})
    const navigate = useNavigate()
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    
    // Initialize all filter states from URL query params if they exist
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
    const [searchInputValue, setSearchInputValue] = useState(searchParams.get('search') || '') // For immediate UI feedback
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
    const [showDeactivated, setShowDeactivated] = useState(searchParams.get('showDeactivated') === 'true')
    const [manufacturedFilter, setManufacturedFilter] = useState(searchParams.get('manufactured') || '')
    const [selectedUom, setSelectedUom] = useState(searchParams.get('uom') || '')
    const [selectedVendor, setSelectedVendor] = useState(searchParams.get('vendor') || '')
    const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '')
    const [selectedCategories, setSelectedCategories] = useState(
        searchParams.get('categories') ? searchParams.get('categories').split(',') : []
    )
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize')) || PaginationComponent.getCurrentPageSize())
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    
    // Bulk selection state
    const [selectedRows, setSelectedRows] = useState([])
    const [selectAll, setSelectAll] = useState(false)

    const [sortColumn, setSortColumn] = useState(searchParams.get('sortColumn') || null);
    const [sortDirection, setSortDirection] = useState(searchParams.get('sortDirection') || null);

    const [brandMap, setBrandMap] = useState({})

    // GTIN copy to clipboard state
    const [showCopyToast, setShowCopyToast] = useState(false)
    const [copiedGtin, setCopiedGtin] = useState('')

    const initialTableState = {
        visibility: {
            selection: true,
            id: false,
            image: true,
            sku: true,
            gtin: true,
            upc: true,
            description: true,
            categories: true,
            manufacturer: true,
            itemSize: true,
            uom: true,
            casePack: true,
            netCaseWeight: true,
            packageWeight: true,
            grossCaseWeight: true,
            ti: true,
            hi: true,
            countryOfOrigin: true,
            length: true,
            width: true,
            height: true,
            volume: true,
            criticalStockLevel: false,
            notes: true,
            totalInventory: true,
            isManufactured: true,
            brand: false
        },
        widths: {
            selection: '60px',
            actions: 'auto',
            stockItemId: 'auto',
            image: 'auto',
            sku: 'auto',
            gtin: 'auto',
            upc: 'auto',
            description: 300,
            categories: 'auto',
            manufacturer: 'auto',
            itemSize: 'auto',
            uom: 'auto',
            casePack: 'auto',
            netCaseWeight: 'auto',
            packageWeight: 'auto',
            grossCaseWeight: 'auto',
            ti: 'auto',
            hi: 'auto',
            countryOfOrigin: 50,
            length: 'auto',
            width: 'auto',
            height: 'auto',
            volume: 'auto',
            criticalStockLevel: 'auto',
            notes: 'auto',
            totalInventory: 'auto',
            isManufactured: 'auto',
            brand: 'auto',
            moreActions: 'auto'
        }
    };

    const { columnState, updateColumnVisibility, updateColumnWidth } = useTableState('stock_items', initialTableState);
    const columnVisibility = columnState.visibility;
    
    const [categoryMap, setCategoryMap] = useState({});
    const [totalPages, setTotalPages] = useState(0);
    const debounceTimeoutRef = useRef(null);
    const [showBarcodeDropdown, setShowBarcodeDropdown] = useState(null);

    const toggleColumnVisibility = (columnId) => {
        updateColumnVisibility(columnId, !columnVisibility[columnId]);
    };

    const columnLabels = {
        id: 'Stock Item ID',
        image: 'Image',
        sku: 'SKU',
        gtin: 'GTIN',
        upc: 'UPC',
        description: 'Description',
        categories: 'Categories',
        manufacturer: 'Manufacturer',
        itemSize: 'Item Size',
        uom: 'UOM',
        casePack: 'Case Pack',
        netCaseWeight: 'Net Case Weight',
        packageWeight: 'Package Weight',
        grossCaseWeight: 'Gross Case Weight',
        ti: 'TI',
        hi: 'HI',
        countryOfOrigin: 'Country of Origin',
        length: 'Length (inch)',
        width: 'Width (inch)',
        height: 'Height (inch)',
        volume: 'Volume',
        criticalStockLevel: 'Critical Stock Level',
        notes: 'Notes',
        totalInventory: 'Total in Inventory',
        isManufactured: 'Manufactured',
        brand: 'Brand'
    };

    useEffect(() => {
        async function fetchStockItems() {
            try {
                setLoading(true);

                // Convert selectedCategories to a query string format
                const selectedCategoriesString = selectedCategories.map(id => `${id}`).join(',');
                const response = await stockItemAPI.getAll(currentPage, pageSize, searchTerm, selectedVendor, selectedCategoriesString, showDeactivated, manufacturedFilter || undefined, selectedBrand || undefined, selectedUom || undefined, sortColumn || undefined, sortDirection || undefined);


                const data = response.results;

                if (!Array.isArray(data)) {
                    throw new Error('Expected an array of stock items');
                }
                setStockItems(data);
                setTotalPages(response.count);
            } catch (error) {
                console.error('Error fetching stock items:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchStockItems();
    }, [currentPage, searchTerm, selectedVendor, selectedBrand, selectedCategories, showDeactivated, manufacturedFilter, selectedUom, pageSize, sortColumn, sortDirection]);

    useEffect(() => {
        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll()
                const mapping = vendors.reduce((acc, vendor) => {
                    acc[vendor._id] = vendor.name
                    return acc
                }, {})
                setVendorMap(mapping)
            } catch (error) {
                console.error('Error fetching vendors:', error)
            }
        }
        fetchVendors()
    }, [])

    useEffect(() => {
        async function fetchBrands() {
            try {
                const response = await brandAPI.getAll(1, 1000, '', false)
                const brands = response.results || []
                const mapping = brands.reduce((acc, brand) => {
                    acc[brand._id] = brand.name
                    return acc
                }, {})
                setBrandMap(mapping)
            } catch (error) {
                console.error('Error fetching brands:', error)
            }
        }
        fetchBrands()
    }, [])

    useEffect(() => {
        async function fetchCategories() {
            try {
                const categories = await stockItemCategoryAPI.getAll();
                const mapping = categories.reduce((acc, category) => {
                    acc[category._id] = category.name;
                    return acc;
                }, {});
                setCategoryMap(mapping);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        }
        fetchCategories();
    }, []);

    // Function to update URL with current filter states
    const updateUrlWithFilters = () => {
        const params = new URLSearchParams();

        // Only add params that have values
        if (searchTerm) params.set('search', searchTerm);
        if (selectedVendor) params.set('vendor', selectedVendor);
        if (selectedBrand) params.set('brand', selectedBrand);
        if (selectedCategories.length > 0) params.set('categories', selectedCategories.join(','));
        if (showDeactivated) params.set('showDeactivated', 'true');
        if (manufacturedFilter) params.set('manufactured', manufacturedFilter);
        if (selectedUom) params.set('uom', selectedUom);
        if (currentPage > 1) params.set('page', currentPage.toString());
        if (pageSize !== PaginationComponent.getCurrentPageSize()) params.set('pageSize', pageSize.toString());
        if (sortColumn) params.set('sortColumn', sortColumn);
        if (sortDirection) params.set('sortDirection', sortDirection);

        // Replace the current URL with the new one including filters
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    // Use this effect to synchronize URL with filter changes
    useEffect(() => {
        updateUrlWithFilters();
    }, [
        searchTerm,
        selectedVendor,
        selectedBrand,
        selectedCategories,
        showDeactivated,
        manufacturedFilter,
        selectedUom,
        currentPage,
        pageSize,
        sortColumn,
        sortDirection,
        location.pathname
    ]);

    // Check for returnFilters when the component mounts
    useEffect(() => {
        // If we have returnFilters in the location state, restore them
        if (location.state?.returnFilters) {
            // Navigate to the same path but with the saved filters from state
            navigate(`${location.pathname}${location.state.returnFilters}`, { 
                replace: true,  // Replace current history entry
                state: {}       // Clear the state to avoid infinite loop
            });
        }
    }, []);  // Only run on mount

    // Keep searchInputValue in sync with searchTerm when it changes from external sources
    useEffect(() => {
        setSearchInputValue(searchTerm);
    }, [searchTerm]);

    const handleToggleActive = async (e, stockItem) => {
        e.stopPropagation();
        
        if (stockItem.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this stock item?')) {
                return;
            }
        }

        try {
            const updatedStockItem = {
                ...stockItem,
                is_active: !stockItem.is_active
            };
            
            await stockItemAPI.update(stockItem._id, updatedStockItem);
            
            setStockItems(stockItems.map(s => 
                s._id === stockItem._id 
                    ? updatedStockItem
                    : s
            ));
        } catch (error) {
            console.error('Error toggling stock item status:', error);
            alert(`Failed to update stock item: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleDelete = (e, item) => {
        e.stopPropagation();
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (itemToDelete) {
            try {
                await stockItemAPI.delete(itemToDelete._id);
                setStockItems(stockItems.filter(item => item._id !== itemToDelete._id));
            } catch (error) {
                console.error('Error deleting stock item:', error);
                const errorMessage = error.response?.data?.error || error.response?.data?.detail || error.response?.data?.message || error.message;
                alert(`Failed to delete stock item: ${errorMessage}`);
            } finally {
                setShowDeleteModal(false);
                setItemToDelete(null);
            }
        }
    };

    const formatVendorOptions = () => {
        return Object.entries(vendorMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const handleVendorFilterChange = (selectedOption) => {
        setSelectedVendor(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const formatBrandOptions = () => {
        return Object.entries(brandMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const handleBrandFilterChange = (selectedOption) => {
        setSelectedBrand(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);

        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchInputChange = (e) => {
        const value = e.target.value;

        // Update the input value immediately for responsive UI
        setSearchInputValue(value);

        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set a new timeout
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value);
            setCurrentPage(1);
            // URL will be updated via the useEffect
        }, 500); // Increased to 500ms for better performance
    };

    // Copy GTIN to clipboard
    const handleCopyGtin = (gtin) => {
        if (!gtin) return;

        navigator.clipboard.writeText(gtin).then(() => {
            setCopiedGtin(gtin);
            setShowCopyToast(true);
            setTimeout(() => setShowCopyToast(false), 2000);
        }).catch((err) => {
            console.error('Failed to copy GTIN:', err);
        });
    };

    const handleCategorySelection = (e, categoryId) => {
        if (e.target.checked) {
            setSelectedCategories(prev => [...prev, categoryId]);
        } else {
            setSelectedCategories(prev => prev.filter(id => id !== categoryId));
        }
        setCurrentPage(1);
    };

    const handleToggleDeactivated = () => {
        setShowDeactivated(prevState => !prevState);
        setCurrentPage(1);
    };

    const handleGenerateReport = async (fileType) => {
        try {
            const columns = getReportColumns('stock_items', columnVisibility);
            if (!columns) {
                alert('No columns selected for report.');
                return;
            }

            setLoading(true);

            const selectedCategoriesString = selectedCategories.map(id => `${id}`).join(',');

            const success = await stockItemAPI.generateStockItemsReport(
                searchTerm,
                selectedVendor,
                selectedCategoriesString,
                showDeactivated,
                fileType,
                columns
            );

            if (!success) {
                alert('Failed to generate report.');
            }
        } catch (error) {
            console.error('Error generating report:', error);
            alert('Failed to generate report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleReceiveClick = (e, stockItemId) => {
        e.stopPropagation()
        // Preserve current filters when navigating to receive page
        const currentFilters = `?${new URLSearchParams({
            search: searchTerm,
            vendor: selectedVendor,
            brand: selectedBrand,
            categories: selectedCategories.join(','),
            showDeactivated: showDeactivated.toString(),
            manufactured: manufacturedFilter,
            uom: selectedUom,
            page: currentPage.toString(),
            pageSize: pageSize.toString(),
            sortColumn: sortColumn || '',
            sortDirection: sortDirection || ''
        }).toString()}`;

        navigate(`/inventory-receive/${stockItemId}`, {
            state: { returnFilters: currentFilters }
        });
    };

    // Selection handlers for bulk operations
    const handleRowSelect = (row, isSelected) => {
        if (isSelected) {
            const selectedItem = {
                id: row._id,
                description: row.description,
                manufacturer: row.manufacturer,
                manufacturer_name: vendorMap[row.manufacturer] || row.manufacturer || 'Unknown',
                gtin: row.gtin,
                sku: row.sku,
                image: row.image,
                case_pack: row.case_pack,
                uom: row.uom,
            }
            setSelectedRows(prev => [...prev, selectedItem])
        } else {
            setSelectedRows(prev => prev.filter(item => item.id !== row._id))
        }
    }

    const handleSelectAll = (isSelected) => {
        setSelectAll(isSelected)
        if (isSelected) {
            const allSelectableRows = stockItems.map(item => ({
                id: item._id,
                description: item.description,
                manufacturer: item.manufacturer,
                manufacturer_name: vendorMap[item.manufacturer] || item.manufacturer || 'Unknown',
                gtin: item.gtin,
                sku: item.sku,
                image: item.image,
                case_pack: item.case_pack,
                uom: item.uom,
            }))
            setSelectedRows(allSelectableRows)
        } else {
            setSelectedRows([])
        }
    }

    const isRowSelected = (rowId) => {
        return selectedRows.some(item => item.id === rowId)
    }

    // Update selectAll state based on current page items and selection
    useEffect(() => {
        if (stockItems.length > 0) {
            const allCurrentPageSelected = stockItems.every(item => 
                selectedRows.some(selectedItem => selectedItem.id === item._id)
            );
            setSelectAll(allCurrentPageSelected);
        } else {
            setSelectAll(false);
        }
    }, [stockItems, selectedRows])

    // Handle bulk receive navigation
    const handleBulkReceive = () => {
        navigate('/bulk-receive', {
            state: {
                selectedItems: selectedRows,
                returnPath: location.pathname + location.search
            }
        });
    }

    const displayedStockItems = stockItems;

    const handleSort = (column, direction) => {
        setSortColumn(column);
        setSortDirection(direction);
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const columns = [
        columnVisibility.selection && {
            id: 'selection',
            header: () => (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="button"
                    tabIndex={-1}
                >
                    <Form.Check
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        label=""
                    />
                </div>
            ),
            cell: (row) => (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="button"
                    tabIndex={-1}
                >
                    <Form.Check
                        type="checkbox"
                        checked={isRowSelected(row._id)}
                        onChange={(e) => {
                            handleRowSelect(row, e.target.checked);
                        }}
                        label=""
                    />
                </div>
            )
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (row) => (
                <ButtonGroup size="sm" className="gap-1">
                    {row.is_active && hasPermission('inventory.receive') && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleReceiveClick(e, row._id);
                            }}
                        >
                            Receive
                        </Button>
                    )}
                    {row.is_active && (
                        <Button
                            variant="info"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/inventory?search=${encodeURIComponent(row.gtin)}`);
                            }}
                            title="View all inventory items with this GTIN"
                        >
                            Inventory
                        </Button>
                    )}
                    {row.is_active && hasPermission('stock.forecast.view') && (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`tooltip-forecast-${row._id}`}>View Stock Forecast</Tooltip>}
                        >
                            <Button
                                variant="info"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/stock-forecast?stockItemId=${row._id}`);
                                }}
                            >
                                <i className="bi bi-graph-up"></i>
                            </Button>
                        </OverlayTrigger>
                    )}
                </ButtonGroup>
            )
        },
        columnVisibility.id && {
            id: 'stockItemId',
            header: 'Stock Item ID',
            cell: (row) => highlightText(row._id, searchTerm)
        },
        columnVisibility.image && {
            id: 'image',
            header: 'Image',
            cell: (row) => row.image && (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            <img 
                                src={row.image} 
                                alt="Stock Item"
                                style={{
                                    width: '300px',
                                    height: '300px',
                                    objectFit: 'contain',
                                    border: '2px solid #122A5A',
                                    borderRadius: '4px',
                                    boxShadow: '0 4px 8px rgba(18, 42, 90, 0.1)',
                                    transition: 'transform 0.2s ease-in-out',
                                }}
                            />
                        </Tooltip>
                    }
                >
                    <img 
                        src={row.image} 
                        alt="Stock Item Thumbnail"
                        style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'contain'
                        }}
                    />
                </OverlayTrigger>
            )
        },
        columnVisibility.sku && {
            id: 'sku',
            header: 'SKU',
            cell: (row) => highlightText(row.sku, searchTerm)
        },
        columnVisibility.gtin && {
            id: 'gtin',
            header: 'GTIN',
            cell: (row) => (
                <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleCopyGtin(row.gtin);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCopyGtin(row.gtin);
                        }
                    }}
                    style={{
                        cursor: 'pointer',
                        borderBottom: '1px dashed #6c757d',
                        transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.borderBottom = '1px solid #0d6efd';
                        e.target.style.color = '#0d6efd';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.borderBottom = '1px dashed #6c757d';
                        e.target.style.color = '';
                    }}
                    title="Click to copy GTIN"
                    aria-label={`Copy GTIN ${row.gtin} to clipboard`}
                >
                    {highlightText(row.gtin, searchTerm)}
                </span>
            )
        },
        columnVisibility.upc && {
            id: 'upc',
            header: 'UPC',
            cell: (row) => highlightText(row.upc, searchTerm)
        },
        columnVisibility.description && {
            id: 'description',
            header: 'Description',
            sortable: true,
            cell: (row) => (
                <>
                    {highlightText(row.description, searchTerm)}
                    {!row.is_active && (
                        <span className="ms-2 text-muted fst-italic">
                            (Inactive)
                        </span>
                    )}
                </>
            )
        },
        columnVisibility.categories && {
            id: 'categories',
            header: () => (
                <Dropdown>
                    <Dropdown.Toggle 
                        variant="light" 
                        id="category-filter-dropdown"
                        style={{
                            fontWeight: 'bold',
                            padding: '0rem 0.5rem 0.35rem',
                            height: '25px'
                        }}
                    >
                        {selectedCategories.length > 0 ? selectedCategories.map(id => categoryMap[id]).join(', ') : 'Categories'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item as="button" onClick={() => setSelectedCategories([])}>
                            <Form.Check 
                                type="checkbox"
                                label="All Categories"
                                checked={selectedCategories.length === 0}
                                onChange={() => setSelectedCategories([])}
                            />
                        </Dropdown.Item>
                        {Object.entries(categoryMap).map(([id, name]) => (
                            <Dropdown.Item as="button" key={id}>
                                <Form.Check 
                                    type="checkbox"
                                    label={name}
                                    checked={selectedCategories.includes(id)}
                                    onChange={(e) => handleCategorySelection(e, id)}
                    />
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            ),
            cell: (row) => row.categories?.map(categoryId => categoryMap[categoryId]).join(', ')
        },
        columnVisibility.manufacturer && {
            id: 'manufacturer',
            header: () => (
                <div style={{ minWidth: '200px' }} className="compact-select">
                    <Select
                        value={selectedVendor ? { value: selectedVendor, label: vendorMap[selectedVendor] } : null}
                        onChange={handleVendorFilterChange}
                        options={formatVendorOptions()}
                        isSearchable
                        isClearable
                        placeholder="Manufacturer"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
            ),
            cell: (row) => vendorMap[row.manufacturer] || row.manufacturer || 'N/A'
        },
        columnVisibility.itemSize && {
            id: 'itemSize',
            header: 'Item Size',
            cell: (row) => row.item_size ? `${row.item_size} ${row.item_size_units}` : 'N/A'
        },
        columnVisibility.uom && {
            id: 'uom',
            header: () => createFilterDropdown({
                selectedValue: selectedUom,
                handleSelect: (value) => { setSelectedUom(value); setCurrentPage(1); },
                items: UOM_MAP,
                defaultLabel: 'UOM',
                allItemsLabel: 'All'
            }),
            cell: (row) => UOM_MAP[row.uom] || row.uom || 'N/A'
        },
        columnVisibility.casePack && {
            id: 'casePack',
            header: 'Case Pack',
            cell: (row) => row.case_pack
        },
        columnVisibility.netCaseWeight && {
            id: 'netCaseWeight',
            header: 'Net Case Weight (lbs)',
            cell: (row) => calculateNetCaseWeight(row.item_size, row.item_size_units, row.case_pack)
        },
        columnVisibility.packageWeight && {
            id: 'packageWeight',
            header: 'Package Weight',
            cell: (row) => row.package_weight && `${row.package_weight}${!row.package_weight_units ? ' lbs' : ` ${row.package_weight_units}`}`
        },
        columnVisibility.grossCaseWeight && {
            id: 'grossCaseWeight',
            header: 'Gross Case Weight (lbs)',
            cell: (row) => {
                const weight = calculateGrossCaseWeight(row.item_size, row.item_size_units, row.case_pack, row.package_weight, row.package_weight_units);
                return isNaN(weight) ? '' : weight;
            }
        },
        columnVisibility.ti && {
            id: 'ti',
            header: 'TI',
            cell: (row) => row.ti
        },
        columnVisibility.hi && {
            id: 'hi',
            header: 'HI',
            cell: (row) => row.hi
        },
        columnVisibility.countryOfOrigin && {
            id: 'countryOfOrigin',
            header: 'Country of Origin',
            cell: (row) => row.country_of_origin && (
                <>
                    {getCountryFlag(row.country_of_origin)}{' '}
                    {row.country_of_origin}{' '}
                </>
            )
        },
        columnVisibility.length && {
            id: 'length',
            header: 'L(inch)',
            cell: (row) => row.dimensions_l
        },
        columnVisibility.width && {
            id: 'width',
            header: 'W(inch)',
            cell: (row) => row.dimensions_w
        },
        columnVisibility.height && {
            id: 'height',
            header: 'H(inch)',
            cell: (row) => row.dimensions_h
        },
        columnVisibility.volume && {
            id: 'volume',
            header: 'Volume',
            cell: (row) => calculateVolumeCubicFeet(row.dimensions_l, row.dimensions_w, row.dimensions_h)
        },
        columnVisibility.criticalStockLevel && {
            id: 'criticalStockLevel',
            header: 'Critical Stock Level',
            cell: (row) => row.critical_stock_level || ''
        },
        columnVisibility.notes && {
            id: 'notes',
            header: 'Notes',
            cell: (row) => (
                <div style={{ 
                    maxWidth: '300px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap' 
                }} title={row.notes || ''}>
                    {row.notes || ''}
                </div>
            )
        },
        columnVisibility.totalInventory && {
            id: 'totalInventory',
            header: 'Total in Inventory',
            sortable: true,
            cell: (row) => {
                const totalInventory = row.total_inventory || 0;
                const criticalLevel = row.critical_stock_level;
                
                // Determine the color based on inventory level
                let color = '';
                if (totalInventory === 0) {
                    color = 'red';
                } else if (criticalLevel && totalInventory < criticalLevel) {
                    color = 'orange';
                }
                
                return (
                    <span style={{ color: color, fontWeight: color ? 'bold' : 'normal' }}>
                        {totalInventory}
                    </span>
                );
            }
        },
        columnVisibility.isManufactured && {
            id: 'isManufactured',
            header: () => (
                <Dropdown>
                    <Dropdown.Toggle
                        variant="light"
                        id="manufactured-filter-dropdown"
                        style={{
                            fontWeight: 'bold',
                            padding: '0rem 0.5rem 0.35rem',
                            height: '25px'
                        }}
                    >
                        {manufacturedFilter === '' ? 'Manufactured' :
                         manufacturedFilter === 'true' ? 'Manufactured' : 'Purchased'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item as="button" onClick={() => { setManufacturedFilter(''); setCurrentPage(1); }}>
                            <Form.Check
                                type="checkbox"
                                label="All"
                                checked={manufacturedFilter === ''}
                                onChange={() => { setManufacturedFilter(''); setCurrentPage(1); }}
                            />
                        </Dropdown.Item>
                        <Dropdown.Item as="button" onClick={() => { setManufacturedFilter('true'); setCurrentPage(1); }}>
                            <Form.Check
                                type="checkbox"
                                label="Manufactured"
                                checked={manufacturedFilter === 'true'}
                                onChange={() => { setManufacturedFilter('true'); setCurrentPage(1); }}
                            />
                        </Dropdown.Item>
                        <Dropdown.Item as="button" onClick={() => { setManufacturedFilter('false'); setCurrentPage(1); }}>
                            <Form.Check
                                type="checkbox"
                                label="Purchased"
                                checked={manufacturedFilter === 'false'}
                                onChange={() => { setManufacturedFilter('false'); setCurrentPage(1); }}
                            />
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            ),
            cell: (row) => row.is_manufactured ? (
                <Badge bg="info">Manufactured</Badge>
            ) : null
        },
        columnVisibility.brand && {
            id: 'brand',
            header: () => (
                <div style={{ minWidth: '200px' }} className="compact-select">
                    <Select
                        value={selectedBrand ? { value: selectedBrand, label: brandMap[selectedBrand] } : null}
                        onChange={handleBrandFilterChange}
                        options={formatBrandOptions()}
                        isSearchable
                        isClearable
                        placeholder="Brand"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
            ),
            cell: (row) => row.brand_name || ''
        },
        {
            id: 'moreActions',
            header: 'More Actions',
            cell: (row) => (
                <ButtonGroup size="sm" className="gap-1">
                    {row.is_active && (
                        <Dropdown show={showBarcodeDropdown === row._id} onToggle={(isOpen) => {
                            setShowBarcodeDropdown(isOpen ? row._id : null);
                        }}>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`tooltip-print-barcode`}>Print Barcode</Tooltip>}
                            >
                                <Dropdown.Toggle 
                                    variant="primary"
                                    size="sm"
                                    tabIndex={-1}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!row.upc) {
                                            navigate(`/print-barcode?gtin=${row.gtin}`);
                                        } else {
                                            setShowBarcodeDropdown(row._id);
                                        }
                                    }}
                                >
                                    <i className="bi bi-printer"></i>
                                </Dropdown.Toggle>
                            </OverlayTrigger>

                            {row.upc && (
                                <Dropdown.Menu>
                                    <Dropdown.Item 
                                        tabIndex={-1}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/print-barcode?gtin=${row.gtin}`);
                                            setShowBarcodeDropdown(null);
                                        }}
                                    >
                                        Print GTIN
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                        tabIndex={-1}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/print-barcode?upc=${row.upc}`);
                                            setShowBarcodeDropdown(null);
                                        }}
                                    >
                                        Print UPC
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            )}
                        </Dropdown>
                    )}
                    {row.is_active && hasPermission('stock.items.modify') && (
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(e, row);
                            }}
                        >
                            Delete
                        </Button>
                    )}
                    {hasPermission('stock.items.modify') && (
                        <Button
                            variant={row.is_active ? "danger" : "success"}
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleActive(e, row);
                            }}
                        >
                            {row.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                    )}
                </ButtonGroup>
            )
        }
    ].filter(Boolean);

    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Stock Items</h1>
                </Col>
                <Col>
                    {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                </Col>
                <Col md={2}>
                    <Dropdown>
                        <Dropdown.Toggle variant="success" id="report-dropdown">
                            Generate Report
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleGenerateReport('csv')}>
                                <i className="material-icons">description</i> Export as CSV
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleGenerateReport('pdf')}>
                                <i className="material-icons">picture_as_pdf</i> Export as PDF
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchInputChange}
                        onClear={handleClearSearch}
                        placeholder="Search by Description, SKU, GTIN or UPC..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button
                        variant="outline-secondary"
                        onClick={handleToggleDeactivated}
                        className="me-2"
                    >
                        {showDeactivated ? 'Hide Deactivated' : 'Show Deactivated'}
                    </Button>
                    {hasPermission('stock.items.modify') && (
                        <Button
                            onClick={() => navigate('/stock-item/create')}
                            variant="success"
                        >
                            Create Stock Item
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    {/* Bulk Actions for Bulk Receive */}
                    {selectedRows.length > 0 && (
                        <Row className="mb-3">
                            <Col>
                                <div className="d-flex align-items-center bg-body-secondary p-2 rounded">
                                    <span className="me-3 text-body">
                                        <strong>{selectedRows.length}</strong> item{selectedRows.length !== 1 ? 's' : ''} selected
                                        {(() => {
                                            // Validate manufacturer consistency
                                            const manufacturers = [...new Set(selectedRows.map(item => item.manufacturer))]
                                            if (manufacturers.length === 1 && manufacturers[0]) {
                                                const manufacturerName = selectedRows[0].manufacturer_name
                                                return <span className="ms-2">from <strong>{manufacturerName}</strong></span>
                                            } else if (manufacturers.length > 1) {
                                                return <span className="ms-2 text-danger">(Mixed manufacturers - cannot bulk receive)</span>
                                            }
                                            return null
                                        })()}
                                    </span>
                                    {(() => {
                                        // Only show Bulk Receive button if all items are from same manufacturer
                                        const manufacturers = [...new Set(selectedRows.map(item => item.manufacturer))]
                                        if (manufacturers.length === 1 && manufacturers[0]) {
                                            return (
                                                <Button 
                                                    variant="success" 
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={handleBulkReceive}
                                                >
                                                    <i className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>inventory</i>
                                                    Bulk Receive
                                                </Button>
                                            )
                                        }
                                        return null
                                    })()}
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        onClick={() => {
                                            setSelectedRows([])
                                            setSelectAll(false)
                                        }}
                                    >
                                        Clear Selection
                                    </Button>
                                    {/* Show warning only if some selected items are not in the current view */}
                                    {selectedRows.length > 0 && stockItems.every(item => !selectedRows.some(row => row.id === item._id)) && (
                                        <span className="text-warning ms-3 small">
                                            <i className="material-icons small align-text-bottom">filter_alt</i> Selected items are not visible with current filters
                                        </span>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}
                    <StickyScrollbar>
                        <ResizableTable
                            columns={columns}
                            data={displayedStockItems}
                            onRowClick={(row) => {
                                if (row.is_active) {
                                    // Preserve current filters when navigating to details page
                                    const currentFilters = `?${new URLSearchParams({
                                        search: searchTerm,
                                        vendor: selectedVendor,
                                        brand: selectedBrand,
                                        categories: selectedCategories.join(','),
                                        showDeactivated: showDeactivated.toString(),
                                        manufactured: manufacturedFilter,
                                        uom: selectedUom,
                                        page: currentPage.toString(),
                                        pageSize: pageSize.toString(),
                                        sortColumn: sortColumn || '',
                                        sortDirection: sortDirection || ''
                                    }).toString()}`;

                                    navigate(`/stock-item/${row._id}`, {
                                        state: {
                                            'initialMode': 'update',
                                            returnFilters: currentFilters
                                        }
                                    });
                                }
                            }}
                            enableSorting={true}
                            onSort={handleSort}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            initialColumnWidths={columnState.widths}
                            tableId="stock_items"
                            striped={true}
                            hover={true}
                            className="table-sm"
                        />
                    </StickyScrollbar>
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message="Are you sure you want to delete this stock item?"
            />

            {/* Toast notification for GTIN copy */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
                <Toast
                    show={showCopyToast}
                    onClose={() => setShowCopyToast(false)}
                    delay={2000}
                    autohide
                    bg="success"
                >
                    <Toast.Body className="text-white">
                        GTIN <strong>{copiedGtin}</strong> copied to clipboard!
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </Container>
    );
}

export default StockItemsListScreen
